import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as ActivityRelationApiActions from '@app/modules/activity-relation/actions/activity-relation-api.actions';
import * as ActivityRelation from '@app/modules/activity-relation/reducers';
import * as ActivityDownload from '@app/modules/activity-download/reducers';
import * as ActivityApiActions from '@app/modules/activity/actions/activity-api.actions';
import * as ActivityDownloadApiActions from '@app/modules/activity-download/actions/activity-download-api.actions';
import { UpdateAvailableParticipantsModel } from '@app/modules/activity/models/update-activity-available-participants.model';
import * as Activity from '@app/modules/activity/reducers';
import * as BackgroundRelationApiActions from '@app/modules/background/actions/background-api.actions';
import * as BackgroundRelation from '@app/modules/background/reducers';
import * as NoteApiActions from '@app/modules/note/actions/note-api.actions';
import { NoteType } from '@app/modules/note/enum/note-type.enum';
import * as Note from '@app/modules/note/reducers';
import { ApplicationType } from '@app/shared/enums/application-type.enum';
import { CompareType } from '@app/shared/models/common/constants';
import { TableFilterModel } from '@app/shared/models/table/table-filter.model';
import { TableSortingModel } from '@app/shared/models/table/table-sorting.model';
import { Store } from '@ngrx/store';
import { NzTabChangeEvent } from 'ng-zorro-antd/tabs';
import { ActivityDownloadUploadModel } from '@app/modules/activity-download/models/activity-download-upload.model';
import * as CertificateApiActions from '@app/modules/certificate/actions/certificate-api.actions';
import * as Certificate from '@app/modules/certificate/reducers';

@Component({
    selector: 'app-activity-view',
    templateUrl: './activity-view.component.html',
    styleUrls: ['./activity-view.component.less'],
})
export class ActivityViewComponent {
    loading$ = this.store.select(Activity.selectViewLoading);
    errors$ = this.store.select(Activity.selectViewErrors);
    activity$ = this.store.select(Activity.selectViewActivity);
    availableParticipants$ = this.store.select(Activity.selectAvailableSpots);
    activityRelations$ = this.store.select(ActivityRelation.selectActivityRelationOverviewStateList);
    activityRelationsWaitlist$ = this.store.select(ActivityRelation.selectActivityRelationWaitlistOverviewStateList);
    backgroundList$ = this.store.select(BackgroundRelation.selectBackgroundList);
    relationList$ = this.store.select(Activity.selectRelations);
    activityRelation$ = this.store.select(ActivityRelation.selectActivityRelation);

    notes$ = this.store.select(Note.selectNoteList);
    note$ = this.store.select(Note.selectNote);

    noteErrors$ = this.store.select(Note.selectErrors);
    noteLoading$ = this.store.select(Note.selectLoading);

    activityDownloads$ = this.store.select(ActivityDownload.selectActivityDownloadOverview);
    activityAssets$ = this.store.select(ActivityDownload.selectAssets);
    activityDefaultAssets$ = this.store.select(ActivityDownload.selectDefaultAssets);
    activityDownloadLoading$ = this.store.select(ActivityDownload.selectLoading);
    activityDownloadErrors$ = this.store.select(ActivityDownload.selectErrors);
    certificates$ = this.store.select(Certificate.selectCertificateOverview);

    sort: TableSortingModel;
    compareType = CompareType;

    activityId: number | null = null;

    constructor(private readonly store: Store) {
        this.activity$.pipe(takeUntilDestroyed()).subscribe((activity) => {
            this.activityId = activity.id;
            
            this.store.dispatch(NoteApiActions.SetNoteType({ noteType: NoteType.ActivityWaitlist, noteTypeId: this.activityId }));
            this.store.dispatch(NoteApiActions.GetOverview());
            this.getParticipants();
            this.getWaitlist();
            this.getActivityAssets();
            this.getActivityDownloads();
            this.getCertificates();
        });

        this.store.dispatch(BackgroundRelationApiActions.GetBackgroundList());
        this.store.dispatch(ActivityDownloadApiActions.GetDefaultAssets());
    }

    updateAvailableParticipants(update: UpdateAvailableParticipantsModel) {
        this.store.dispatch(ActivityApiActions.UpdateParticipants(update));
    }

    addReservation() {
        this.store.dispatch(ActivityRelationApiActions.AddReservation());
    }

    deleteParticipant(event: { id: number; isReservation: boolean }) {
        if (event.isReservation) {
            this.store.dispatch(ActivityRelationApiActions.DeleteReservation({ id: event.id }));
        } else {
            this.store.dispatch(ActivityRelationApiActions.Delete({ id: event.id }));
        }
    }

    private getActivityAssets() {
        this.store.dispatch(ActivityDownloadApiActions.GetAssets({ activityId: this.activityId }));
    }

    private getActivityDownloads() {
        this.store.dispatch(ActivityDownloadApiActions.FilterOverview({
            activityId: this.activityId,
            pagination: {
                page: 1,
                pageSize: -1,
            },
            sorting: {
                propertyName: 'Name',
                order: 'ASC',
            }
        }));
    }

    getParticipants() {
        const newFilter = [
            { column: 'ApplicationType', value: ApplicationType.Reservation.toString(), operand: CompareType.EqualTo },
            { column: 'ApplicationType', value: ApplicationType.Normal.toString(), operand: CompareType.EqualTo },
        ] as TableFilterModel[];
        this.store.dispatch(
            ActivityRelationApiActions.FilterOverview({
                activityId: this.activityId,
                pagination: {
                    page: 1,
                    pageSize: -1,
                },
                sorting: {
                    propertyName: 'Role',
                    order: 'ASC',
                },
                filter: newFilter,
                thenBy: [{ propertyName: 'CreatedOn', order: 'ASC' }],
            }),
        );
    }

    getWaitlist() {
        const newFilter = {
            column: 'ApplicationType',
            value: ApplicationType.Waitlist.toString(),
            operand: CompareType.EqualTo,
        } as TableFilterModel;
        this.store.dispatch(
            ActivityRelationApiActions.FilterWaitlistOverview({
                activityId: this.activityId,
                pagination: {
                    page: 1,
                    pageSize: -1,
                },
                sorting: {
                    propertyName: 'Role',
                    order: 'ASC',
                },
                filter: [newFilter] as TableFilterModel[],
                thenBy: [{ propertyName: 'CreatedOn', order: 'ASC' }],
            }),
        );
    }

    searchRelation(event: any) {
        this.store.dispatch(ActivityApiActions.GetRelations(event));
    }

    reset() {
        this.store.dispatch(ActivityApiActions.EmptyRelations());
    }

    createActivityRelation(event: any) {
        Object.keys(event).includes('id')
            ? this.store.dispatch(ActivityRelationApiActions.Update(event))
            : this.store.dispatch(ActivityRelationApiActions.Create(event));
    }

    getRelation(id: number) {
        this.store.dispatch(ActivityRelationApiActions.GetActivityRelation({ id }));
    }

    createNote(event: any) {
        this.store.dispatch(
            NoteApiActions.Create({ ...event, activityId: this.activityId, noteType: NoteType.ActivityWaitlist }),
        );
    }

    editNote(event: any) {
        this.store.dispatch(
            NoteApiActions.Update({
                data: {
                    ...event,
                    noteMessageEdit: event.noteMessageEdit,
                    activityId: this.activityId,
                    noteType: NoteType.ActivityWaitlist,
                },
            }),
        );
    }

    setNoteImportant(event: any) {
        this.store.dispatch(NoteApiActions.SetImportant({ noteId: event.noteId }));
    }

    deleteNote(event: any) {
        this.store.dispatch(NoteApiActions.Delete({ noteId: event.noteId }));
    }

    uploadActivityDownload(event: ActivityDownloadUploadModel) {
        this.store.dispatch(ActivityDownloadApiActions.Upload(event));
    }

    deleteActivityDownload(event: { id: number }) {
        this.store.dispatch(ActivityDownloadApiActions.Delete(event));
    }

    getCertificates() {
        this.store.dispatch(
            CertificateApiActions.FilterOverview({
                pagination: {
                    page: 1,
                    pageSize: -1,
                },
                sorting: {
                    propertyName: 'GeneratedOn',
                    order: 'DESC',
                },
                activityId: this.activityId
            }),
        );
    }

    deleteCertificate(event: {id: number}) {
        this.store.dispatch(CertificateApiActions.Delete(event));
    }

    noteSidebarEnabled = false;
    tabSize = 24;
    selectedTab(selectedTabEvent: NzTabChangeEvent) {
        if (selectedTabEvent['index'] === 1) {
            this.noteSidebarEnabled = true;
            this.tabSize = 18;
        } else {
            this.tabSize = 24;
            this.noteSidebarEnabled = false;
        }
    }
}
